/**
 * RequireRole.tsx — the presentation-only role gate (R41).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: /patients, /clinicians and /admin are role-scoped. This gate is UI
 * convenience ONLY — the gateway authorises every call server-side, so a user
 * who hand-edits the URL sees a sign-in prompt or a deny message, never data.
 * While the session hydrates we hold a neutral loading state to avoid a flash
 * of the wrong screen.
 *
 * NOT SIGNED IN (no session, expired session, or just signed out) → an
 * immediate REDIRECT to /auth carrying `?next=<here>`, so the person is
 * prompted to (re)log in and lands back where they were (CTO direction,
 * 2026-07-16 — no static "please sign in" page).
 *
 * Wrong-role UX is a message + a single CTA back to the caller's own portal
 * (homeForRole) — never an auto-redirect, so the person understands why they
 * landed here before being moved.
 *
 * Unverified PATIENT (standalone /auth register, emailed link not yet followed)
 * → check-email. The dashboard is for a confirmed mailbox; journey checkout
 * already verified via OTP and never hits this gate.
 */
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import type { UserRole } from '../api/types';
import { PageFrame, Spinner, Title, Help, ButtonArrow, buttonClass, buttonStyle } from '../ui/kit';
import { homeForRole, homeLabelForRole, CHECK_EMAIL_PATH } from './roleHome';

/** Render `children` only when a signed-in user holds `role`. */
export function RequireRole({ role, children }: { role: UserRole; children: ReactNode }) {
  const { me, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <PageFrame narrow>
        <Spinner label="Checking your session…" />
      </PageFrame>
    );
  }

  if (!me) {
    // Expired/absent session → straight to the sign-in prompt, with the way back.
    return <Navigate to={`/auth?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (me.role !== role) {
    return (
      <PageFrame narrow>
        <Title>You don&rsquo;t have access to this area</Title>
        <Help>This area is for a different type of account than the one you&rsquo;re signed in with.</Help>
        <Link to={homeForRole(me.role)} className={buttonClass('cta')} style={buttonStyle('cta')}>
          {homeLabelForRole(me.role)}
          <ButtonArrow />
        </Link>
      </PageFrame>
    );
  }

  if (role === 'PATIENT' && !me.emailVerified) {
    return <Navigate to={CHECK_EMAIL_PATH} replace />;
  }

  return <>{children}</>;
}
